import { useParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface FieldMapping {
  [key: string]: string;
}

export function SmartCorrectConfigPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: service, isLoading } = useQuery({
    queryKey: ['smart-correct', id],
    queryFn: () =>
      api
        .get('/api/smart-correct/get', { params: { service_instance_id: id } })
        .then((r) => r.data),
    enabled: !!id,
  });

  const s = service?.query?.[0] ?? service;
  const config = s?.config ?? {};

  // Fetch catalogs for the Vantage instance associated with this SC instance
  const vantageAppId = config?.vantage?.app_id;
  const { data: catalogsData } = useQuery({
    queryKey: ['vantage-catalogs', vantageAppId],
    queryFn: () =>
      api.get('/api/smart-correct/vantage-catalogs', { params: { app_instance_id: vantageAppId } }).then((r) => r.data),
    enabled: !!vantageAppId,
  });
  const catalogs = catalogsData ?? [];

  const { register, handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      matchField: config?.matching?.matchField || 'name',
      fuzzyThreshold: config?.matching?.fuzzyThreshold || 70,
      fuzzyCaseSensitive: config?.matching?.fuzzyCaseSensitive || false,
      catalogId: config?.vantage?.catalog_id || config?.catalog?.catalogId || '',
      service_title: s?.service_title || '',
    },
  });

  const fieldMappings: FieldMapping = config?.fieldMapping ?? {
    'Vendor ID': 'vendor_id',
    'Vendor Name': 'name',
    'Vendor Address': 'street',
    'Vendor City': 'city',
    'Vendor Country': 'country',
    'Vendor Postal Code': 'postal_code',
    'Vendor Tax ID': 'tax_id',
    'Vendor Phone': 'phone',
    'Vendor Email': 'email',
    'Vendor Bank Account': 'bank_account',
  };

  const updateMutation = useMutation({
    mutationFn: (data: any) =>
      api.put(`/api/smart-correct/${id}/config`, data),
    onSuccess: () => {
      toast.success('Configuration updated');
      queryClient.invalidateQueries({ queryKey: ['smart-correct', id] });
      navigate(`/smart-correct/${id}`);
    },
    onError: () => toast.error('Failed to update configuration'),
  });

  function onSubmit(formData: any) {
    updateMutation.mutate({
      matching: {
        matchField: formData.matchField,
        fuzzyThreshold: Number(formData.fuzzyThreshold),
        fuzzyCaseSensitive: formData.fuzzyCaseSensitive,
      },
      catalog: {
        catalogId: formData.catalogId,
        fields: fieldMappings,
      },
      vantage: {
        app_id: config?.vantage?.app_id || vantageAppId,
        catalog_id: formData.catalogId,
      },
      fieldMapping: fieldMappings,
    });
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading configuration...</div>;
  }

  if (!s) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Service not found</p>
        <Link to="/smart-correct"><Button variant="outline">Back to Smart Correct</Button></Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to={`/smart-correct/${id}`}>
          <Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#cc0404]" />
            Configuration
          </h1>
          <p className="text-gray-500 text-sm">{s.service_title}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl">
        {/* Vantage Instance Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vantage Instance</CardTitle>
            <CardDescription>The ABBYY Vantage instance and data catalog this service uses.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">Vantage App ID:</span>{' '}
                <span className="font-medium">{config?.vantage?.app_id || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Data Catalog:</span>{' '}
                <span className="font-medium">{config?.vantage?.catalog_id || '—'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Matching Config */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Matching Configuration</CardTitle>
            <CardDescription>How Smart Correct matches vendors from OCR data.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Match Field</Label>
              <Select defaultValue={watch('matchField')} onValueChange={(val) => setValue('matchField', val)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Vendor Name</SelectItem>
                  <SelectItem value="address">Delivery Address</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Fuzzy Search Threshold: {watch('fuzzyThreshold')}%</Label>
              <p className="text-sm text-gray-500">
                Minimum similarity score (0-100) to consider a match.
              </p>
              <input
                type="range"
                min={0}
                max={100}
                step={5}
                {...register('fuzzyThreshold', { valueAsNumber: true })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#cc0404]"
              />
              <div className="flex justify-between text-xs text-gray-400">
                <span>0 (Match anything)</span>
                <span>70 (Default)</span>
                <span>100 (Exact only)</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="fuzzyCaseSensitive"
                {...register('fuzzyCaseSensitive')}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="fuzzyCaseSensitive" className="font-normal">
                Case-sensitive matching
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Catalog Config */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Catalog Configuration</CardTitle>
            <CardDescription>Select the Vantage catalog to search against.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Data Catalog</Label>
              {catalogs.length === 0 ? (
                <p className="text-sm text-gray-400">No catalogs available for this Vantage instance</p>
              ) : (
                <Select defaultValue={watch('catalogId')} onValueChange={(val) => setValue('catalogId', val)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {catalogs.map((catalog: any) => (
                      <SelectItem key={catalog.id} value={catalog.id}>
                        {catalog.name || catalog.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Field Mapping */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Field Mapping</CardTitle>
            <CardDescription>
              Map Vantage extraction fields to internal vendor fields.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="text-sm font-medium text-gray-500">Vantage Field</div>
              <div className="text-sm font-medium text-gray-500">Internal Field</div>
            </div>
            {Object.entries(fieldMappings).map(([vantageField, internalField]) => (
              <div key={vantageField} className="grid grid-cols-2 gap-3">
                <Input value={vantageField} disabled className="bg-gray-50" />
                <Input value={internalField} disabled className="bg-gray-50" />
              </div>
            ))}
            <p className="text-xs text-gray-400 mt-2">
              Field mapping is defined at service creation. Contact support to modify.
            </p>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save Configuration'}
          </Button>
          <Link to={`/smart-correct/${id}`}>
            <Button type="button" variant="outline">Cancel</Button>
          </Link>
        </div>
      </form>
    </div>
  );
}